import { ICourse, ICourseGrade, IGrade, TExamType } from '@/types'
import { ScanResponse, ScanRow } from '@/api/aiScanner'

function normalizeGrade(value: string | null | undefined): number | null {
    if (!value) return null
    const s = value.toString().trim().toLowerCase()

    const m = s.match(/([1-6])/)
    if (m) return Number(m[1])

    return null
}

function mapExamType(eksForm: string | null | undefined): TExamType {
    const s = (eksForm || '').trim().toUpperCase()
    if (s === 'S') return 'Skriftlig eksamen'
    if (s === 'M') return 'Muntlig eksamen'
    if (s === 'MP' || s === 'PM') return 'Muntlig-praktisk eksamen'
    return ''
}

function normalizeName(input: string): string {
    return input
        .toLowerCase()
        .replace(/\./g, '')
        .replace(/,/g, '')
        .replace(/\s+/g, ' ')
        .trim()
}

function simplifyForMatching(name: string): string {
    let s = normalizeName(name)

    const removeTokens = [
        'skriftleg',
        'skriftlig',
        'munnleg',
        'muntlig',
        'skr',
        'mu',
        'internasj',
        'internasjonal',
    ]

    for (const t of removeTokens) {
        s = s.replace(new RegExp(`\\b${t}\\b`, 'g'), '')
    }

    s = s.replace(/\s+/g, ' ').trim()
    return s
}

function normalizeCode(input: string): string {
    return (input || '').toUpperCase().replace(/\s+/g, '').trim()
}

function makeUnknownId(seed: string): number {
    let h = 0
    for (let i = 0; i < seed.length; i++) {
        h = (h * 31 + seed.charCodeAt(i)) >>> 0
    }
    return 1000 + (h % 900000)
}

function makeUnknownCourse(row: ScanRow, index: number): ICourse {
    const code = normalizeCode(row.kode || '') || `UNKNOWN_${index}`
    const name = (row.fag || '').trim() || code

    return {
        id: makeUnknownId(`${code}|${name}|${index}`),
        name,
        code,
        sciencePoints: 0,
        twentythreeFiveRule: false,
        sciencePointsGroup: null,
        courseType: 'unknown' as any,
        hasTwoPrivateExams: false,
        replacedCourses: [],
    }
}

function buildCourseLookups(courses: ICourse[]) {
    const byCode = new Map<string, ICourse>()
    const byName = new Map<string, ICourse>()
    const bySimplifiedName = new Map<string, ICourse>()

    const setIfMissing = (
        map: Map<string, ICourse>,
        key: string,
        value: ICourse
    ) => {
        if (!key) return
        if (!map.has(key)) {
            map.set(key, value)
        }
    }

    for (const c of courses) {
        if (c.code) {
            setIfMissing(byCode, normalizeCode(c.code), c)
        }

        if (c.name) {
            setIfMissing(byName, normalizeName(c.name), c)
            setIfMissing(bySimplifiedName, simplifyForMatching(c.name), c)
        }

        const replaced = (c as any).replacedCourses
        if (Array.isArray(replaced)) {
            for (const r of replaced) {
                if (r?.code) {
                    setIfMissing(byCode, normalizeCode(String(r.code)), c)
                }
                if (r?.name) {
                    setIfMissing(byName, normalizeName(String(r.name)), c)
                    setIfMissing(
                        bySimplifiedName,
                        simplifyForMatching(String(r.name)),
                        c
                    )
                }
            }
        }
    }

    return { byCode, byName, bySimplifiedName }
}

function findMatchInCourses(
    row: ScanRow,
    courses: ICourse[]
): ICourse | undefined {
    const { byCode, byName, bySimplifiedName } = buildCourseLookups(courses)

    const kode = normalizeCode(row.kode || '')
    const fag = (row.fag || '').trim()

    let course: ICourse | undefined = kode ? byCode.get(kode) : undefined

    if (!course && fag) {
        course = byName.get(normalizeName(fag))
    }

    if (!course && fag) {
        course = bySimplifiedName.get(simplifyForMatching(fag))
    }

    return course
}

function rowToICourseGrade(course: ICourse, row: ScanRow): ICourseGrade {
    const grades: IGrade[] = []

    const standpunkt = normalizeGrade(row.standpunkt)
    if (standpunkt !== null) {
        grades.push({ type: 'grade', grade: standpunkt, examType: '' })
    }

    const eks = normalizeGrade(row.eks_karakter)
    if (eks !== null) {
        grades.push({
            type: 'exam',
            grade: eks,
            examType: mapExamType(row.eks_form),
        })
    }

    return { course, grades }
}

export function mapScanToCourseGrades(
    scan: ScanResponse,
    defaultCourses: ICourse[],
    additionalCourses: ICourse[],
    legacyCourses: ICourse[]
): {
    mapped: ICourseGrade[]
    unknownCodes: { kode: string; fag?: string | null }[]
    matchedFromAdditional: ICourse[]
    matchedFromLegacy: ICourse[]
    unknownCourses: ICourse[]
} {
    const additionalById = new Map(additionalCourses.map((c) => [c.id, c]))
    const legacyById = new Map(legacyCourses.map((c) => [c.id, c]))

    const mapped: ICourseGrade[] = []
    const unknownCodes: { kode: string; fag?: string | null }[] = []
    const matchedFromAdditional: ICourse[] = []
    const matchedFromLegacy: ICourse[] = []
    const unknownCourses: ICourse[] = []

    const rows: ScanRow[] = scan.courses
        ? Object.values(scan.courses)
        : scan.rows || []

    for (let i = 0; i < rows.length; i++) {
        const r = rows[i]
        const kode = (r.kode || '').trim()

        let course =
            findMatchInCourses(r, defaultCourses) ||
            findMatchInCourses(r, additionalCourses) ||
            findMatchInCourses(r, legacyCourses)

        if (!course) {
            const unknown = makeUnknownCourse(r, i)
            unknownCourses.push(unknown)

            if (kode) {
                unknownCodes.push({ kode, fag: r.fag })
            }

            mapped.push(rowToICourseGrade(unknown, r))
            continue
        }

        if (additionalById.has(course.id)) {
            matchedFromAdditional.push(course)
        }

        if (legacyById.has(course.id)) {
            matchedFromLegacy.push(course)
        }

        mapped.push(rowToICourseGrade(course, r))
    }

    return {
        mapped,
        unknownCodes,
        matchedFromAdditional,
        matchedFromLegacy,
        unknownCourses,
    }
}

export function mergeGrades(
    existing: ICourseGrade[],
    incoming: ICourseGrade[]
): ICourseGrade[] {
    const byCourseKey = new Map<string, ICourseGrade>()

    const makeKey = (item: ICourseGrade) => {
        const code = normalizeCode(item.course.code || '')
        const name = normalizeName(item.course.name || '')
        if (code) return `code:${code}`
        return `name:${name}`
    }

    for (const g of existing) {
        byCourseKey.set(makeKey(g), g)
    }

    for (const inc of incoming) {
        byCourseKey.set(makeKey(inc), inc)
    }

    return Array.from(byCourseKey.values())
}